import {
  Dialog,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { AlertType, appendAlertItem, AuthenticationState } from '@pruforce/common-adminweb-sdk';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ProTableCell from 'src/app/common/components/ProTable/ProTableCell';
import StickyTableCell from 'src/app/common/components/ProTable/StickyTableCell';
import { PruForm } from 'src/app/common/components/PruForm';
import PruTableEmptyRow from 'src/app/common/components/Table/PruTableEmptyRow';
import PruTableLoading from 'src/app/common/components/Table/PruTableLoading';
import PruTablePaginationActions from 'src/app/common/components/Table/PruTablePaginationActions';
import { regionLocale } from 'src/app/i18n';
import { addContent, editContent, getContents, getTitles } from 'src/app/modules/Event/network/greetingCardCrud';
import { getSpecialDays } from 'src/app/modules/Event/network/specialDayCrud';
import { SpecialDayItem } from 'src/app/modules/Event/types/special-day-type';
import { RootState } from 'src/redux/store';
import { getSpecialLabel, getTitleGreetingLabel } from '../../../../network/utils';
import { PublishStatus } from '../../../../types/greeting-card-type';
import { styles } from '../styles';
import PopUp from './components/popUp';
import filterConfig from './filterConfig';
import tableConfig from './tableConfig';
import tableHeader from './tableHeader';

const ContentList: FC<any> = (props) => {
  const intl = useIntl();
  const { classes } = styles();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const filterInitData = {
    titleId: undefined,
    content: '',
    modifier: '',
  };

  const [filterData, setFilterData] = useState<{ [key: string]: any }>(filterInitData);
  const [filterSavedData, setFilterSavedData] = useState<{ [key: string]: any }>(filterInitData);
  const [tableData, setTableData] = useState<{ [key: string]: any }[]>([]);
  const [pageData, setPageData] = useState<{ [key: string]: any }>({
    pageNum: 1,
    pageSize: 10,
  });
  const [listData, setListData] = useState<{ [key: string]: any }>({ specialList: [], titleList: [] });
  const [listSpecialDay, setListSpecialDay] = useState<SpecialDayItem[]>([]);

  const [popUpVisible, setPopUpVisible] = useState<boolean>(false);
  const initialDefaultValue = {
    titleId: undefined,
  };

  const [initialValues, setInitialValues] = useState<{ [key: string]: any }>(initialDefaultValue);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [contentId, setContentId] = useState<string>('');
  const { user } = useSelector<RootState, AuthenticationState>((state) => state.auth);

  const fetchAPI = async () => {
    await fetchData();
    await initialListData();
  };

  useEffect(() => {
    fetchAPI();
  }, []);

  useEffect(() => {
    window.addEventListener('keypress', keyPressSearch);
    return () => {
      window.removeEventListener('keypress', keyPressSearch);
    };
    // eslint-disable-next-line
  }, [filterData]);

  const initialListData = async () => {
    const newListData: any = {
      specialList: [],
      titleList: [],
    };
    const [specificDay, titles] = await Promise.all([
      getSpecialDays({
        status: PublishStatus.PUBLISHED,
        pageSize: 1000,
        pageNum: 1,
      }),
      getTitles({
        status: PublishStatus.PUBLISHED,
        pageSize: 1000,
        pageNum: 1,
      }),
    ]);
    if (specificDay.values) {
      const options = specificDay.values.map((item: any) => ({
        label: item?.name ? item?.name : getSpecialLabel(item, intl.locale, false),
        value: item?.specialDayId,
      }));
      newListData.specialList = options;
      setListSpecialDay(specificDay.values);
    }

    if (titles.data?.values) {
      const options = titles.data.values.map((item: any) => ({
        label: getTitleGreetingLabel(item, intl.locale),
        value: item.titleId,
      }));
      newListData.titleList = [{ label: Translation('component.status.all'), value: '' }, ...options];
    }

    setListData(newListData);
  };

  const fetchData = (newFilterState?: any) => {
    setPageLoading(true);
    const newFilter = newFilterState || {};
    const requestParams = {
      ...filterData,
      ...pageData,
      ...newFilter,
    };

    if (!requestParams.titleId) {
      delete requestParams.titleId;
    }

    if (requestParams.status == PublishStatus.ALL) {
      delete requestParams.status;
    }

    if (!requestParams.content || requestParams.content == '') {
      delete requestParams.content;
    }

    if (!requestParams.modifier || requestParams.modifier == '') {
      delete requestParams.modifier;
    }

    getContents(requestParams)
      .then((res: any) => {
        setTableData(res.data?.values || []);
        setPageData({
          pageNum: res.data?.page || 1,
          pageSize: res.data?.size || 0,
          total: res.data?.total || 0,
        });
      })
      .catch((err: any) => {})
      .finally(() => {
        setPageLoading(false);
      });
  };

  const onSearch = () => {
    // setFilterData(filterData)
    const newPageData = {
      ...pageData,
      pageNum: 1,
    };
    setPageData(newPageData);
    setFilterSavedData(filterData);
    fetchData({ ...filterData, ...newPageData });
  };

  const keyPressSearch = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onChangePage = (pageNum: number) => {
    const newPageData = {
      ...pageData,
      pageNum,
    };
    setPageData(newPageData);
    fetchData({ ...filterSavedData, ...newPageData });
  };

  const onChangePageRows = (rows: any) => {
    const newPageData = {
      ...pageData,
      pageNum: 1,
      pageSize: rows,
    };

    setPageData(newPageData);
    fetchData({ ...filterSavedData, ...newPageData });
  };

  const handleSort = (sorter: any) => {
    let sortItem;
    let sortOrder;
    Object.entries(sorter).forEach(([item, order]) => {
      sortItem = item;
      sortOrder = order;
    });
    const data = {
      ...filterSavedData,
      sortItem,
      sortOrder: sortOrder ? (sortOrder as string).toUpperCase() : sortOrder,
    };

    const newPageData = {
      ...pageData,
      pageNum: 1,
    };

    setFilterSavedData(data);
    setPageData(newPageData);
    fetchData({ ...data, ...newPageData });
  };

  const openPopUp = () => {
    setPopUpVisible(true);
  };

  const onSave = async (values: any) => {
    setFormLoading(true);
    const { content1, content2 } = values;
    const i18nDataList = [
      {
        lang: regionLocale.length > 0 && regionLocale[0],
        content: content1,
      },
      {
        lang: regionLocale.length > 0 && regionLocale[1],
        content: content2,
      },
    ];
    const data = {
      ...values,
      i18nDataList,
      contentId,
      specificDay: listSpecialDay.find((item: any) => item.specialDayId === values.specificDay) || null,
      modifier: user && user.username,
    };

    try {
      if (contentId) {
        await editContent(data);
      } else {
        await addContent(data);
      }

      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: Translation('global.submit.success'),
            content: Translation('global.submit.success'),
          },
        ]),
      );
      await refreshData();
      setPopUpVisible(false);
    } catch (error: any) {
      if (error?.code === 'E010') {
        return dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: `Content cannot be ${contentId ? 'updated' : 'created'} for unpublished special days`,
            },
          ]),
        );
      }

      return dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: 'Failed, please try again.',
          },
        ]),
      );
    } finally {
      setFormLoading(false);
    }
  };

  const refreshData = async () => {
    const newPageData = {
      ...pageData,
      pageNum: 1,
    };
    setPageData(newPageData);
    fetchData({ ...filterSavedData, ...newPageData });
  };

  const getValidSpecificDay = (specificDay?: string) =>
    specificDay && listSpecialDay.some((item: any) => item.specialDayId === specificDay) ? specificDay : null;

  const onEdit = (row: any) => {
    setContentId(row.contentId);
    const i18nDataList = row.i18nDataList && row.i18nDataList;
    const content1 = i18nDataList && i18nDataList[0].content;
    const content2 = i18nDataList && i18nDataList[1].content;
    setPopUpVisible(true);
    setInitialValues({
      ...row,
      ...{
        content1,
        content2,
        contentId: row.contentId,
        specificDay: getValidSpecificDay(row.specificDay?.specialDayId),
      },
    });
  };

  const onCopy = (row: any) => {
    const i18nDataList = row.i18nDataList && row.i18nDataList;
    const content1 = i18nDataList && i18nDataList[0].content;
    const content2 = i18nDataList && i18nDataList[1].content;

    setInitialValues({
      ...row,
      ...{
        content1,
        content2,
        contentId: row.contentId,
        specificDay: getValidSpecificDay(row.specificDay?.specialDayId),
      },
      status: PublishStatus.UNPUBLISHED,
      modifier: user && user.username,
    });
    setContentId('');
    setPopUpVisible(true);
  };

  const onChangeStatus = (row: any) => {
    editContent({
      ...row,
      status: row.status == PublishStatus.PUBLISHED ? PublishStatus.UNPUBLISHED : PublishStatus.PUBLISHED,
      modifier: user && user.username,
    })
      .then((res: any) => {
        refreshData();
      })
      .catch((err: any) => {
        if (err?.code === 'E010' && row?.status == PublishStatus.UNPUBLISHED) {
          return dispatch(
            appendAlertItem([
              {
                severity: AlertType.ERROR,
                title: 'Error',
                content: 'Content cannot be published when the associated special day is unpublished',
              },
            ]),
          );
        }
        return dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: 'Failed, please try again.',
            },
          ]),
        );
      });
  };

  const onCancel = () => {
    setPopUpVisible(false);
    setInitialValues(initialDefaultValue);
    setContentId('');
    setTimeout(() => {
      setIsView(false);
    }, 200);
  };

  const onReset = () => {
    setFilterData(filterInitData);
  };

  return (
    <>
      <PruForm
        config={filterConfig(Translation, onSearch, onReset, listData)}
        data={filterData}
        onChange={setFilterData}
        space={20}
        style={{ padding: 20 }}
        opts={{ labelWidth: 120 }}
      ></PruForm>

      <PruForm
        config={tableHeader(Translation, openPopUp, refreshData)}
        data={[]}
        onChange={() => {}}
        space={20}
        style={{ padding: '15px 20px', marginTop: 20, borderTopLeftRadius: 5, borderTopRightRadius: 5 }}
      ></PruForm>

      <TableContainer component={Paper} className="event-category-container">
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {tableConfig(Translation, regionLocale).map((item) => (
                <ProTableCell key={item.prop} dataIndex={item.prop} sorter={item.sorter} handleSort={handleSort}>
                  {item.title}
                </ProTableCell>
              ))}
              <StickyTableCell className={`sticky-cell`}>
                {Translation('component.formLabel.operation')}
              </StickyTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <PruTableLoading isLoading={pageLoading} />
            <PruTableEmptyRow isEmpty={tableData.length === 0} />
            {tableData.length > 0 &&
              tableData.map((row) => (
                <TableRow key={row.id}>
                  {tableConfig(Translation, regionLocale).map((config) => (
                    <TableCell key={config.prop}>{config.render ? config.render(row) : row[config.prop]}</TableCell>
                  ))}
                  <StickyTableCell className={`sticky-cell`}>
                    <div className={classes.operationContainer}>
                      {row.status == PublishStatus.UNPUBLISHED && (
                        <div
                          style={{ marginRight: 10 }}
                          className={`${classes.operationBtn}`}
                          onClick={() => onEdit(row)}
                        >
                          {Translation('component.formTitle.edit')}
                        </div>
                      )}
                      {
                        <div
                          style={{ marginRight: 10 }}
                          className={`${classes.operationBtn}`}
                          onClick={() => onChangeStatus(row)}
                        >
                          {row.status == PublishStatus.PUBLISHED
                            ? Translation('app.button.unpublish')
                            : Translation('app.button.publish')}
                        </div>
                      }
                      <div
                        style={{ marginRight: 10 }}
                        className={`${classes.operationBtn}`}
                        onClick={() => onCopy(row)}
                      >
                        Copy
                      </div>
                    </div>
                  </StickyTableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableFooter component="div" className={`table-footer-css`}>
        <TableRow component="div">
          <TablePagination
            align="right"
            rowsPerPageOptions={[5, 10, 20, 50]}
            colSpan={12}
            component="div"
            count={pageData.total || 0}
            rowsPerPage={pageData.pageSize}
            page={pageData.pageNum - 1 || 0}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            }}
            onPageChange={(e, page) => onChangePage(page + 1)}
            onRowsPerPageChange={(e) => onChangePageRows(e.target.value)}
            ActionsComponent={PruTablePaginationActions}
          />
        </TableRow>
      </TableFooter>

      <Dialog open={popUpVisible} onClose={isView ? onCancel : () => {}} fullWidth>
        <DialogTitle>
          {isView
            ? Translation('global.view.text')
            : contentId
            ? Translation('global.edit.text')
            : Translation('app.button.addNew')}{' '}
          {`${Translation('event.greeting_card.content.form')}`}
        </DialogTitle>
        <PopUp
          key={contentId || initialValues.contentId || 'new-content'}
          visible={popUpVisible}
          onCancel={onCancel}
          onSave={onSave}
          i18ConfigData={regionLocale}
          titleList={listData.titleList}
          specialList={listData.specialList}
          initialValues={initialValues}
          confirmLoading={formLoading}
          disabled={isView}
        />
      </Dialog>
    </>
  );
};

export default ContentList;
